import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { PlanSidebarState } from '@/lib/plan-sidebar-state';

interface PlanSidebarContextValue {
  isOpen: boolean;
  toggle: () => void;
  open: () => void;
  close: () => void;
}

const PlanSidebarContext = createContext<PlanSidebarContextValue | null>(null);

export function PlanSidebarProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(() => PlanSidebarState.isOpen());

  useEffect(() => {
    const handleStateChange = (event: CustomEvent) => {
      setIsOpen(event.detail.isOpen);
    };
    window.addEventListener('plan-sidebar-state-changed', handleStateChange as EventListener);
    return () => window.removeEventListener('plan-sidebar-state-changed', handleStateChange as EventListener);
  }, []);

  const toggle = useCallback(() => {
    const newState = !isOpen;
    setIsOpen(newState);
    PlanSidebarState.setOpen(newState);
  }, [isOpen]);

  const open = useCallback(() => {
    setIsOpen(true);
    PlanSidebarState.setOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    PlanSidebarState.setOpen(false);
  }, []);

  return (
    <PlanSidebarContext.Provider value={{ isOpen, toggle, open, close }}>
      {children}
    </PlanSidebarContext.Provider>
  );
}

export function usePlanSidebar() {
  const context = useContext(PlanSidebarContext);
  if (!context) {
    throw new Error('usePlanSidebar must be used within PlanSidebarProvider');
  }
  return context;
}
